import {ConfirmationDialog, date, DetailColumns, DetailPane, name, useDialogs} from '@management-ui/core';
import {Delete, RestoreFromTrash} from '@mui/icons-material';
import * as React from 'react';
import {useCallback, useContext, useMemo} from 'react';
import {ServiceContext} from '../Services';
import TaskStatus from '../TaskStatus';
import TaskAdmin from './Admin';
import Notes from '../Notes';
import {Paper, styled} from '@mui/material';
import TaskPriority from '../TaskPriority';
import {useSelector} from 'react-redux';

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const Priority = styled('span')(({theme}) => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  gap: theme.spacing(1),
}));

const Footer = styled(Paper)(({theme}) => ({
  '&.MuiPaper-root': {
    overflow: 'hidden',
    marginTop: theme.spacing(2),
  }
}));

const Detail = ({task, onTaskUpdated, onLoading, onDeleted, onRestored, onChangeTask}) => {
  const services = useContext(ServiceContext);
  const user = useSelector(state => state['auth'].user);
  const userCanManage = useMemo(() => user.permissions.can_manage_tasks, [user]);
  const {openDialogs, toggleDialog} = useDialogs(['delete', 'restore']);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      onLoading(true);
      services.task.deleteTask(task).then(() => {
        onLoading(false);
        onDeleted();
      }).catch(() => onLoading(false));
    }
  }, [services, task, onLoading, onDeleted, toggleDialog]);

  const handleRestore = useCallback((confirmed) => {
    toggleDialog('restore', false);
    if (confirmed) {
      onLoading(true);
      services.task.restoreTask(task).then((restored) => {
        onLoading(false);
        onRestored();
        onTaskUpdated(restored);
      }).catch(() => onLoading(false));
    }
  }, [services, task, onTaskUpdated, onLoading, onRestored, toggleDialog]);

  return task?.id ? (
    <Container>
      <DetailColumns columns={[

        <DetailPane
          title="Task Details"
          actions={userCanManage ? [
            ...(task.archived ? [
              {title: 'Restore', icon: <RestoreFromTrash/>, onClick: () => toggleDialog('restore', true)},
            ] : [
              {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
            ])
          ] : []}
          details={[
            {title: 'Title', value: task.title},
            {title: 'Assignee', value: name(task.assignee)},
            {title: 'Description', value: task.description ?? '-'},
            {title: 'Deadline', value: date(task.deadline)},
            {title: 'Priority', value: <Priority><TaskPriority task={task}/><span>{task.priority}</span></Priority>},
            {title: 'Recurs', value: task.schedule ? task.schedule.title : 'N/A'},
            {title: 'Status', value: <TaskStatus task={task}/>}
          ]}
          dialogs={[
            (props) => (
              <ConfirmationDialog
                {...props}
                open={openDialogs['delete'] ?? false}
                title="Delete Task"
                message="Are you sure you want to delete this task?"
                onClose={handleDelete}
              />
            ),
            (props) => (
              <ConfirmationDialog
                {...props}
                open={openDialogs['restore'] ?? false}
                title="Restore Task"
                message="Are you sure you want to restore this task?"
                onClose={handleRestore}
              />
            )
          ]}
        />,

        <TaskAdmin task={task} onTaskUpdated={onTaskUpdated} onChangeTask={onChangeTask} onLoading={onLoading}/>
      ]}/>

      <Footer>
        <Notes
          entity={task}
          onEntityUpdated={onTaskUpdated}
          type="task"
          onSave={({comment}) => services.task.note.saveNote(task, {comment})}
        />
      </Footer>
    </Container>
  ) : null;
};

export default Detail;
